// AdminOrganizations — SUPERADMIN-only.
// Lists all organizations, lets the superadmin create new orgs and invite an
// ORG_ADMIN for each org via emailed invite link.
import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card, Badge, Table, Toolbar } from './_shared';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Building2, Mail, Copy, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function AdminOrganizations() {
  const { user } = useAuth();
  const isSuper = user?.platformRole === 'SUPERADMIN';
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create-org dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', industry: '', description: '', domain: '' });
  const [creating, setCreating] = useState(false);

  // Invite-admin dialog
  const [inviteFor, setInviteFor] = useState(null); // org being invited into
  const [invitee, setInvitee] = useState({ email: '', firstName: '', lastName: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/orgs'); setOrgs(r.data?.organizations || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load organizations'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createOrg = async (e) => {
    e?.preventDefault();
    if (!newOrg.name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      await api.post('/orgs', newOrg);
      toast.success(`Organization "${newOrg.name}" created`);
      setNewOrg({ name: '', industry: '', description: '', domain: '' });
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create organization');
    } finally { setCreating(false); }
  };

  const sendInvite = async (e) => {
    e?.preventDefault();
    if (!invitee.email.trim()) { toast.error('Email is required'); return; }
    setInviting(true); setInviteResult(null);
    try {
      const r = await api.post(`/orgs/${inviteFor.org_id}/invite-admin`, invitee);
      setInviteResult(r.data);
      toast.success('Invitation sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send invitation');
    } finally { setInviting(false); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };

  const closeInvite = () => {
    setInviteFor(null);
    setInvitee({ email: '', firstName: '', lastName: '' });
    setInviteResult(null);
    setCopied(false);
  };

  if (!isSuper) {
    return (
      <AdminLayout title="Organizations" subtitle="Tenants">
        <Card className="p-10 text-center">
          <Building2 className="w-10 h-10 mx-auto text-slate-500 mb-3"/>
          <h2 className="text-lg text-slate-100 mb-1">Superadmin only</h2>
          <p className="text-sm text-slate-400">Organization management is restricted to the Capexara superadmin.</p>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Organizations" subtitle="Tenants"
      actions={
        <Button onClick={() => setShowCreate(true)} data-testid="btn-create-org" className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white tracking-[0.15em] uppercase text-[12px]">
          <Plus className="w-4 h-4 mr-1.5"/> New Organization
        </Button>
      }>
      <Toolbar>
        <div className="text-xs text-slate-500">
          {orgs.length} {orgs.length === 1 ? 'organization' : 'organizations'} · Capexara-managed multi-tenant.
        </div>
      </Toolbar>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>
      ) : (
        <Table
          getKey={(r) => r.org_id}
          columns={[
            { key: 'name', header: 'Organization', render: (r) => (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#5790C9]"/>
                <span className="text-slate-100" data-testid={`org-name-${r.org_id}`}>{r.name}</span>
                {r.is_default && <Badge tone="orange">DEFAULT</Badge>}
                {!r.isActive && <Badge tone="red">INACTIVE</Badge>}
              </div>
            )},
            { key: 'industry', header: 'Industry', render: (r) => r.industry || <span className="text-slate-600">—</span> },
            { key: 'domain',   header: 'Domain',   render: (r) => r.domain || <span className="text-slate-600">—</span> },
            { key: 'memberCount', header: 'Members', render: (r) => <span className="tabular-nums">{r.memberCount ?? 0}</span> },
            { key: 'projectCount', header: 'Projects', render: (r) => <span className="tabular-nums">{r.projectCount ?? 0}</span> },
            { key: 'createdAt', header: 'Created', render: (r) => (r.createdAt || '').slice(0,10) || '—' },
            { key: 'actions', header: '', render: (r) => (
              <div className="flex justify-end">
                <button onClick={() => setInviteFor(r)} data-testid={`btn-invite-admin-${r.org_id}`}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 rounded border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white transition">
                  <Mail className="w-3.5 h-3.5"/> Invite Admin
                </button>
              </div>
            )},
          ]}
          rows={orgs}
          empty="No organizations yet. Click 'New Organization' to onboard your first tenant."
        />
      )}

      {/* Create-org dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0B1220] border-slate-800 text-slate-100 sm:max-w-md" data-testid="dialog-create-org">
          <DialogHeader>
            <DialogTitle>New Organization</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              The new tenant will start empty. Invite an Organization Admin once it's created.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createOrg} className="space-y-3">
            <div>
              <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Name *</Label>
              <Input value={newOrg.name} onChange={e => setNewOrg(o => ({...o, name: e.target.value}))} required data-testid="org-input-name" className="bg-slate-900 border-slate-800"/>
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Industry</Label>
              <Input value={newOrg.industry} onChange={e => setNewOrg(o => ({...o, industry: e.target.value}))} data-testid="org-input-industry" className="bg-slate-900 border-slate-800"/>
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Primary domain</Label>
              <Input value={newOrg.domain} onChange={e => setNewOrg(o => ({...o, domain: e.target.value}))} placeholder="acme.com" data-testid="org-input-domain" className="bg-slate-900 border-slate-800"/>
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Description</Label>
              <Input value={newOrg.description} onChange={e => setNewOrg(o => ({...o, description: e.target.value}))} data-testid="org-input-desc" className="bg-slate-900 border-slate-800"/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="border-slate-700 text-slate-300">Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="org-submit" className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white tracking-[0.15em] uppercase text-[12px]">
                {creating ? 'Creating…' : 'Create Organization'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite-admin dialog */}
      <Dialog open={!!inviteFor} onOpenChange={(o) => !o && closeInvite()}>
        <DialogContent className="bg-[#0B1220] border-slate-800 text-slate-100 sm:max-w-md" data-testid="dialog-invite-admin">
          <DialogHeader>
            <DialogTitle>Invite Organization Admin</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Send an emailed invitation link for <span className="text-slate-300">{inviteFor?.name}</span>. The invitee will set their own password.
            </DialogDescription>
          </DialogHeader>
          {!inviteResult ? (
            <form onSubmit={sendInvite} className="space-y-3">
              <div>
                <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Email *</Label>
                <Input type="email" required value={invitee.email} onChange={e => setInvitee(v => ({...v, email: e.target.value}))} data-testid="invite-input-email" className="bg-slate-900 border-slate-800"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">First name</Label>
                  <Input value={invitee.firstName} onChange={e => setInvitee(v => ({...v, firstName: e.target.value}))} data-testid="invite-input-fn" className="bg-slate-900 border-slate-800"/>
                </div>
                <div>
                  <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Last name</Label>
                  <Input value={invitee.lastName} onChange={e => setInvitee(v => ({...v, lastName: e.target.value}))} data-testid="invite-input-ln" className="bg-slate-900 border-slate-800"/>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeInvite} className="border-slate-700 text-slate-300">Cancel</Button>
                <Button type="submit" disabled={inviting} data-testid="invite-submit-btn" className="bg-[#F26B1F] hover:bg-[#d55812] text-white tracking-[0.15em] uppercase text-[12px]">
                  {inviting ? 'Sending…' : 'Send Invitation'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md p-3 border border-emerald-500/40 bg-emerald-500/5">
                <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-400 mb-1">Invitation created</div>
                <p className="text-xs text-slate-300">Email queued via Resend. Share the link below if needed.</p>
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.15em] uppercase text-slate-400">Invite link</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input readOnly value={inviteResult.invite_link} data-testid="invite-link" className="bg-slate-900 border-slate-800 text-slate-200 text-xs font-mono"/>
                  <button onClick={() => copyLink(inviteResult.invite_link)}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded border border-slate-700 hover:border-slate-500 text-slate-300 text-xs">
                    {copied ? <><Check className="w-3.5 h-3.5"/> Copied</> : <><Copy className="w-3.5 h-3.5"/> Copy</>}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Expires {inviteResult.invitation?.expiresAt?.slice(0,10)}.</p>
              </div>
              <DialogFooter>
                <Button onClick={closeInvite} className="bg-[#1E4E8C] hover:bg-[#143A6B] text-white tracking-[0.15em] uppercase text-[12px]">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
